import { useTranslation } from "react-i18next";
import { isNil, memoize } from "lodash-es";
import BigNumber from "bignumber.js";
import { openTelegramLink } from "@telegram-apps/sdk-react";
import { CHAIN_IDS } from "@uxuycom/web3-tg-sdk";
import useSWR from "swr";
import { useNavigate } from "react-router";
import { TokenWithBalance } from "~/types";
import { Page } from "~/components/Page/Page";
import useQueryTradePairAndBalance from "./useQueryTradePairAndBalance";
import Loading from "~/components/Loading";
import QuickBuySwapHeader from "./QuickBuySwapHeader";
import InsufficientBalance from "./InsufficientBalance";
import { FooterBtn } from "~/components/FooterBtn";
import { createOrder, fetchOrderQuota, submitOrder } from "~/api/endpoints";
import { queryCachedSlippage } from "../Trade/hooks/useSlippageSetting";
import {
    caculateSlippageValue,
    getWalletReceiveLink,
    isBuiltInToken,
} from "../Trade/utils";
import { useUXUYWallet } from "~/Provider/UXUYWallet";
import useAsyncAction from "~/hook/useAsyncAction";
import { useSnackbar } from "~/Provider/Snackbar";
import { useMemo, useState } from "react";
import QuickBuyOrderDetail from "./QuickBuyOrderDetail";
import BalanceInfo from "./BalanceInfo";
import { isSufficientBalance } from "./TradeError";
import { xyLogEvent } from "~/hook/useFirebase";

type QuickBuyConfirmWrapperProps = {
    chain_id: string;
    contract: string;
    from_amount: number;
};

type QuickBuyConfirmProps = {
    chain_id: string;
    contract: string;
    from_amount: number;
    from_price: string;
    to_price: string;
    from: TokenWithBalance;
    to: TokenWithBalance;
};

const QuickBuyConfirm: React.FC<QuickBuyConfirmProps> = ({
    from,
    to,
    from_amount,
    from_price,
    to_price,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { getAddress, sendTransaction } = useUXUYWallet();
    const fromTokenBase = from.token_base;
    const toTokenBase = to.token_base;
    const fromTokenBalance = from.balance;
    const nowBalance = new BigNumber(fromTokenBalance);
    const payAmount = new BigNumber(from_amount);
    const [isInsufficientBalance, setIsInsufficientBalance] = useState(
        nowBalance.lte(payAmount)
    );
    const [errMsg, setErrorMsg] = useState<string>();

    const queryOrderQuota = memoize(async () => {
        if (!isInsufficientBalance) return;

        const slippageValue = await queryCachedSlippage();
        const slippage = caculateSlippageValue(slippageValue);

        try {
            const orderQuota = await fetchOrderQuota({
                from_chain_id: Number(fromTokenBase.chain_id),
                from_contract: fromTokenBase.contract,
                from_amount: String(from_amount),
                to_chain_id: Number(toTokenBase.chain_id),
                to_contract: toTokenBase.contract,
                to_amount: "0",
                slippage: slippage,
            });

            const router = orderQuota.data.routers[0];
            return router;
        } catch (err: any) {
            if ("code" in err) {
                snackbar.error(err.message);
            }
            console.error(err);
            throw err;
        }
    });

    const { data: orderQuotaData, isLoading: orderQuotaLoading } = useSWR(
        ["QuickBuyQueryQuota", isInsufficientBalance],
        queryOrderQuota,
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            errorRetryCount: 0,
        }
    );

    const executeCreateOrder = memoize(async () => {
        if (isInsufficientBalance) return;
        const slippageValue = await queryCachedSlippage();
        const isAutomaticSlippage =
            slippageValue == null || slippageValue.length === 0;
        const slippage = caculateSlippageValue(slippageValue);

        const address = getAddress(String(fromTokenBase.chain_id));
        if (!address) {
            throw new Error("No user wallet address!");
        }

        try {
            const orderInfo = await createOrder({
                from_address: address,
                from_chain_id: Number(fromTokenBase.chain_id),
                from_contract: fromTokenBase.contract,
                from_amount: String(from_amount),

                to_address: address,
                to_chain_id: Number(toTokenBase.chain_id),
                to_contract: toTokenBase.contract,
                to_amount: "0",
                slippage: slippage,
                is_auto_slippage: isAutomaticSlippage,
                order_type: 1,
                direction: 1,
            });
            xyLogEvent("QuickBuy:CreateOrderSuccess", {
                type: "buy",
                from: {
                    chain_id: fromTokenBase.chain_id,
                    contract: fromTokenBase.contract,
                },
                to: {
                    chain_id: toTokenBase.chain_id,
                    contract: toTokenBase.contract,
                },
                order: { id: orderInfo.data.order_id },
            });
            return orderInfo;
        } catch (err: any) {
            xyLogEvent("QuickBuy:CreateOrderFailed", {
                type: "buy",
                from: {
                    chain_id: fromTokenBase.chain_id,
                    contract: fromTokenBase.contract,
                },
                to: {
                    chain_id: toTokenBase.chain_id,
                    contract: toTokenBase.contract,
                },
                err: err,
            });
            if ("code" in err) {
                if (isSufficientBalance(err.code)) {
                    setIsInsufficientBalance(true);
                    setErrorMsg(err.message);
                }
                snackbar.error(err.message);
            }
            console.error(err);
            throw err;
        }
    });

    const {
        data: createdOrderData,
        isLoading: createdOrderLoading,
        error: createdOrderError,
    } = useSWR("QuickBuyCreateOrder", executeCreateOrder, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        errorRetryCount: 0,
    });

    const onConfirm = async () => {
        if (isInsufficientBalance) {
            const receiveLink = getWalletReceiveLink(
                String(fromTokenBase.chain_id),
                fromTokenBase.contract
            );
            return openTelegramLink(receiveLink);
        } else {
            if (createdOrderError) {
                console.log(createdOrderError);
                if ("code" in createdOrderError) {
                    snackbar.error(createdOrderError.message);
                }
                return;
            }

            if (!createdOrderData) {
                snackbar.error("Can not create order!");
                return;
            }

            const tradeOrder = createdOrderData.data;
            const chainId = String(tradeOrder.swap_info.from.chain_id);

            xyLogEvent("QuickBuy:Start", {
                type: "buy",
                from: {
                    chain_id: fromTokenBase.chain_id,
                    contract: fromTokenBase.contract,
                },
                to: {
                    chain_id: toTokenBase.chain_id,
                    contract: toTokenBase.contract,
                },
                order: { id: tradeOrder?.order_id },
            });

            try {
                xyLogEvent("QuickBuy:SignStart", {
                    type: "buy",
                    from: {
                        chain_id: fromTokenBase.chain_id,
                        contract: fromTokenBase.contract,
                    },
                    to: {
                        chain_id: toTokenBase.chain_id,
                        contract: toTokenBase.contract,
                    },
                    order: { id: tradeOrder?.order_id },
                });
                const transactionCode = await sendTransaction(
                    chainId,
                    tradeOrder
                );
                xyLogEvent("QuickBuy:SignSuccessed", {
                    type: "buy",
                    from: {
                        chain_id: fromTokenBase.chain_id,
                        contract: fromTokenBase.contract,
                    },
                    to: {
                        chain_id: toTokenBase.chain_id,
                        contract: toTokenBase.contract,
                    },
                    order: { id: tradeOrder?.order_id },
                });
                const isSolana = chainId === String(CHAIN_IDS.SOLANA);
                if (isSolana) {
                    await submitOrder(
                        tradeOrder.order_id,
                        undefined,
                        transactionCode
                    );
                } else {
                    await submitOrder(
                        tradeOrder.order_id,
                        transactionCode,
                        undefined
                    );
                }

                xyLogEvent("QuickBuy:Successed", {
                    type: "buy",
                    from: {
                        chain_id: fromTokenBase.chain_id,
                        contract: fromTokenBase.contract,
                    },
                    to: {
                        chain_id: toTokenBase.chain_id,
                        contract: toTokenBase.contract,
                    },
                    order: { id: tradeOrder?.order_id },
                });

                navigate(
                    `/trade-detail?order_id=${tradeOrder.order_id}&chain_id=${toTokenBase.chain_id}&contract=${toTokenBase.contract}`,
                    {
                        replace: true,
                    }
                );
            } catch (err: any) {
                xyLogEvent("QuickBuy:Interrupted", {
                    type: "buy",
                    from: {
                        chain_id: fromTokenBase.chain_id,
                        contract: fromTokenBase.contract,
                    },
                    to: {
                        chain_id: toTokenBase.chain_id,
                        contract: toTokenBase.contract,
                    },
                    err: err,
                });

                console.log(err);
                if ("code" in err) {
                    snackbar.error(err.message);
                }
            }
        }
    };

    const confirmAction = useAsyncAction(onConfirm);
    const fromValue = useMemo(() => {
        if (isInsufficientBalance) {
            return BigNumber(from_amount)
                .times(BigNumber(from_price))
                .toNumber()
                .toString();
        } else {
            return createdOrderData?.data?.swap_info?.from?.value ?? "0";
        }
    }, [from_amount, from_price, isInsufficientBalance, createdOrderData]);

    const toAmount = useMemo(() => {
        if (isInsufficientBalance) {
            return orderQuotaData?.amount ?? "0";
        } else {
            return createdOrderData?.data?.swap_info?.to?.amount ?? "0";
        }
    }, [isInsufficientBalance, orderQuotaData, createdOrderData]);

    const toValue = useMemo(() => {
        if (isInsufficientBalance) {
            return BigNumber(toAmount)
                .times(BigNumber(to_price))
                .toNumber()
                .toString();
        } else {
            return createdOrderData?.data?.swap_info?.to?.value ?? "0";
        }
    }, [toAmount, to_price, isInsufficientBalance, createdOrderData]);

    return (
        <Page bgThemeKey="bg_bottom">
            {isInsufficientBalance ? (
                <InsufficientBalance errMsg={errMsg} />
            ) : (
                <div className="h-5"></div>
            )}
            <QuickBuySwapHeader
                fromTokenBase={fromTokenBase}
                toTokenBase={toTokenBase}
                fromAmount={String(from_amount)}
                fromValue={fromValue}
                isLoading={
                    isInsufficientBalance
                        ? orderQuotaLoading
                        : createdOrderLoading
                }
                toAmount={toAmount}
                toValue={toValue}
            />
            {isInsufficientBalance ? (
                <BalanceInfo
                    fromTokenBase={fromTokenBase}
                    fromBalance={fromTokenBalance}
                />
            ) : (
                <QuickBuyOrderDetail
                    order={createdOrderData?.data}
                    isLoading={createdOrderLoading}
                />
            )}
            <FooterBtn
                className="h-[54px]"
                ifPlaceholder={true}
                text={
                    isInsufficientBalance
                        ? `${t("deposit")} ${fromTokenBase.symbol.toUpperCase()}`
                        : `${t("buy")} ${toTokenBase.symbol.toUpperCase()}`
                }
                onClick={confirmAction.run}
                btnLoading={confirmAction.isLoading || createdOrderLoading}
                type="primary"
            />
        </Page>
    );
};

const QuickBuyConfirmWrapper: React.FC<QuickBuyConfirmWrapperProps> = ({
    chain_id,
    contract,
    from_amount,
}) => {
    const { data, isLoading } = useQueryTradePairAndBalance(chain_id, contract);
    const isBuiltIn = isBuiltInToken(contract);
    const fromToken = isBuiltIn ? data?.to : data?.from;
    const fromTokenPrice = fromToken?.price;
    const toToken = isBuiltIn ? data?.from : data?.to;
    const toTokenPrice = toToken?.price;

    if (
        isLoading ||
        isNil(fromToken) ||
        isNil(toToken) ||
        isNil(fromTokenPrice) ||
        isNil(toTokenPrice)
    ) {
        return (
            <Page>
                <Loading />
            </Page>
        );
    }

    return (
        <QuickBuyConfirm
            chain_id={chain_id}
            contract={contract}
            from={fromToken}
            from_amount={from_amount}
            from_price={fromTokenPrice}
            to={toToken}
            to_price={toTokenPrice}
        />
    );
};

export default QuickBuyConfirmWrapper;
